import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Search, 
  BookOpen, 
  Video, 
  MessageCircle, 
  FileText, 
  ChevronRight,
  Users,
  Truck,
  FolderKanban,
  Calendar,
  BarChart3,
  Settings,
  HelpCircle,
  Lightbulb,
  PlayCircle
} from 'lucide-react';
import { HELP_CONTENT } from '@/data/helpContent';

const HELP_CATEGORIES = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: BookOpen,
    description: 'Learn the basics of EQUIPR Horizon',
    articles: [
      { id: 'gs-1', title: 'Welcome to EQUIPR Horizon', readTime: '3 min' },
      { id: 'gs-2', title: 'Understanding the Dashboard', readTime: '5 min' },
      { id: 'gs-3', title: 'Navigation & Menu Overview', readTime: '2 min' },
      { id: 'gs-4', title: 'User Roles & Permissions', readTime: '4 min' },
    ]
  },
  {
    id: 'equipment',
    title: 'Equipment Management',
    icon: Truck,
    description: 'Managing your equipment database',
    articles: [
      { id: 'eq-1', title: 'Adding New Equipment', readTime: '3 min' },
      { id: 'eq-2', title: 'Equipment Categories & Types', readTime: '4 min' },
      { id: 'eq-3', title: 'Tracking Maintenance Schedules', readTime: '5 min' },
      { id: 'eq-4', title: 'Equipment Compliance & Certifications', readTime: '6 min' },
    ]
  },
  {
    id: 'personnel',
    title: 'Personnel Management',
    icon: Users,
    description: 'Managing your workforce',
    articles: [
      { id: 'pe-1', title: 'Adding Personnel Records', readTime: '3 min' },
      { id: 'pe-2', title: 'FIFO & Rotation Schedules', readTime: '5 min' },
      { id: 'pe-3', title: 'Certifications & Compliance', readTime: '4 min' },
      { id: 'pe-4', title: 'Medical Records & Inductions', readTime: '4 min' },
      { id: 'pe-5', title: 'Using the Personnel Gantt View', readTime: '6 min' },
    ]
  },
  {
    id: 'projects',
    title: 'Project Management',
    icon: FolderKanban,
    description: 'Creating and managing projects',
    articles: [
      { id: 'pr-1', title: 'Creating a New Project', readTime: '4 min' },
      { id: 'pr-2', title: 'Project Budgeting & Forecasting', readTime: '6 min' },
      { id: 'pr-3', title: 'Allocating Resources to Projects', readTime: '5 min' },
      { id: 'pr-4', title: 'Project Status & Lifecycle', readTime: '3 min' },
    ]
  },
  {
    id: 'planners',
    title: 'Planning Tools',
    icon: Calendar,
    description: 'Using the visual planners',
    articles: [
      { id: 'pl-1', title: 'Equipment Planner Overview', readTime: '5 min' },
      { id: 'pl-2', title: 'Personnel Planner Overview', readTime: '5 min' },
      { id: 'pl-3', title: 'Drag & Drop Allocations', readTime: '4 min' },
      { id: 'pl-4', title: 'Timeline Views (Day/Week/Month)', readTime: '3 min' },
    ]
  },
  {
    id: 'reports',
    title: 'Reports & Analytics',
    icon: BarChart3,
    description: 'Understanding your data',
    articles: [
      { id: 'rp-1', title: 'Dashboard Metrics Explained', readTime: '4 min' },
      { id: 'rp-2', title: 'Utilisation Reports', readTime: '5 min' },
      { id: 'rp-3', title: 'Compliance Alerts & Warnings', readTime: '4 min' },
    ]
  },
  {
    id: 'admin',
    title: 'Administration',
    icon: Settings,
    description: 'System configuration & settings',
    articles: [
      { id: 'ad-1', title: 'Managing Users', readTime: '4 min' },
      { id: 'ad-2', title: 'Data Management (Categories)', readTime: '5 min' },
      { id: 'ad-3', title: 'System Settings', readTime: '3 min' },
    ]
  },
];

const QUICK_LINKS = [
  { title: 'Video Tutorials', icon: Video, href: '#tutorials' },
  { title: 'Documentation', icon: FileText, href: '#docs' },
  { title: 'Contact Support', icon: MessageCircle, href: '#support' },
];

export default function HelpSheet({ open, onOpenChange }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Filter categories and articles based on search
  const filteredCategories = HELP_CATEGORIES.filter(cat => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      cat.title.toLowerCase().includes(query) ||
      cat.description.toLowerCase().includes(query) ||
      cat.articles.some(a => a.title.toLowerCase().includes(query))
    );
  });

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedArticle(null);
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
  };

  const handleBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
  };

  const renderArticleContent = () => {
    const content = HELP_CONTENT[selectedArticle?.id];
    
    if (!content) {
      return (
        <div className="text-center py-8 text-slate-500">
          <HelpCircle className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p>Content not found</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
          <Badge variant="secondary">{content.category}</Badge>
          <span>•</span>
          <span>{content.readTime} read</span>
        </div>
        
        {/* Video placeholder - for future use */}
        {content.videoUrl && (
          <div className="bg-slate-100 border border-slate-200 rounded-lg p-4 flex items-center gap-3">
            <PlayCircle className="w-8 h-8 text-slate-500" />
            <div>
              <p className="font-medium text-slate-800 text-sm">Video Tutorial Available</p>
              <p className="text-xs text-slate-500">Watch the video guide for this topic</p>
            </div>
            <Button variant="outline" size="sm" className="ml-auto">Watch</Button>
          </div>
        )}

        {/* Article sections */}
        <div className="space-y-6">
          {content.sections.map((section, idx) => (
            <div key={idx}>
              <h3 className="text-base font-semibold text-slate-800 mb-2">{section.heading}</h3>
              <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                {section.content.split('\n').map((line, lineIdx) => {
                  // Handle bullet points
                  if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
                    return (
                      <div key={lineIdx} className="flex gap-2 ml-2 my-1">
                        <span className="text-slate-400">•</span>
                        <span>{line.trim().replace(/^[•\-]\s*/, '')}</span>
                      </div>
                    );
                  }
                  // Handle bold text with **
                  if (line.includes('**')) {
                    const parts = line.split(/\*\*(.*?)\*\*/g);
                    return (
                      <p key={lineIdx} className="my-1">
                        {parts.map((part, partIdx) => 
                          partIdx % 2 === 1 
                            ? <strong key={partIdx} className="font-semibold text-slate-800">{part}</strong>
                            : part
                        )}
                      </p>
                    );
                  }
                  // Regular paragraph
                  return line.trim() ? <p key={lineIdx} className="my-1">{line}</p> : null;
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Tips section */}
        {content.tips && content.tips.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
            <h4 className="text-amber-800 font-semibold text-sm mb-2 flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Pro Tips
            </h4>
            <ul className="space-y-1">
              {content.tips.map((tip, idx) => (
                <li key={idx} className="text-amber-700 text-sm flex gap-2">
                  <span>•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Related articles */}
        {content.relatedArticles && content.relatedArticles.length > 0 && (
          <div className="border-t border-slate-200 pt-4 mt-6">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Related Articles</h4>
            <div className="flex flex-wrap gap-2">
              {content.relatedArticles.map((relId) => {
                const related = HELP_CONTENT[relId];
                if (!related) return null;
                return (
                  <button
                    key={relId}
                    onClick={() => {
                      // Find the category for this article
                      const cat = HELP_CATEGORIES.find(c => c.articles.some(a => a.id === relId));
                      if (cat) {
                        setSelectedCategory(cat);
                        setSelectedArticle(cat.articles.find(a => a.id === relId));
                      }
                    }}
                    className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-600 transition-colors"
                  >
                    {related.title}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Feedback */}
        <div className="border-t border-slate-200 pt-4 mt-6">
          <p className="text-sm text-slate-500 mb-3">Was this article helpful?</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">👍 Yes</Button>
            <Button variant="outline" size="sm">👎 No</Button>
          </div>
        </div>
      </div>
    );
  };

  const renderCategoryArticles = () => {
    return (
      <div className="space-y-2">
        {selectedCategory.articles.map((article) => (
          <button
            key={article.id}
            onClick={() => handleArticleClick(article)}
            className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
          >
            <div>
              <div className="font-medium text-slate-800">{article.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">{article.readTime} read</div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>
        ))}
      </div>
    );
  };

  const renderCategories = () => {
    return (
      <div className="space-y-3">
        {filteredCategories.map((category) => {
          const IconComponent = category.icon;
          return (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category)}
              className="w-full flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
            >
              <div className="p-2 bg-slate-100 rounded-lg">
                <IconComponent className="w-5 h-5 text-slate-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800">{category.title}</div>
                <div className="text-sm text-slate-500 mt-0.5">{category.description}</div>
                <div className="text-xs text-slate-400 mt-1">{category.articles.length} articles</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 mt-2" />
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-slate-200 bg-white">
            <SheetHeader className="text-left">
              <SheetTitle className="text-xl font-bold tracking-tight text-slate-900">
                Help & Support
              </SheetTitle>
              <SheetDescription className="text-sm text-slate-500">
                Find answers, tutorials, and support resources
              </SheetDescription>
            </SheetHeader>
            
            {/* Search */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="help-search"
              />
            </div>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1">
            <div className="p-4">
              {/* Back button */}
              {(selectedCategory || selectedArticle) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleBack}
                  className="mb-4 -ml-2 text-slate-600"
                >
                  ← Back
                </Button>
              )}

              {/* Breadcrumb */}
              {selectedCategory && (
                <div className="flex items-center gap-2 text-sm mb-4">
                  <span className="text-slate-400">Help</span>
                  <ChevronRight className="w-3 h-3 text-slate-400" />
                  <span className={selectedArticle ? 'text-slate-400' : 'text-slate-800 font-medium'}>
                    {selectedCategory.title}
                  </span>
                  {selectedArticle && (
                    <>
                      <ChevronRight className="w-3 h-3 text-slate-400" />
                      <span className="text-slate-800 font-medium truncate">{selectedArticle.title}</span>
                    </>
                  )}
                </div>
              )}

              {/* Main content area */}
              {selectedArticle ? (
                renderArticleContent()
              ) : selectedCategory ? (
                <>
                  <div className="mb-4">
                    <h2 className="text-lg font-semibold text-slate-800">{selectedCategory.title}</h2>
                    <p className="text-sm text-slate-500">{selectedCategory.description}</p>
                  </div>
                  {renderCategoryArticles()}
                </>
              ) : (
                <>
                  {/* Quick Links */}
                  {!searchQuery && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3">Quick Links</h3>
                      <div className="grid grid-cols-3 gap-2">
                        {QUICK_LINKS.map((link) => {
                          const IconComponent = link.icon;
                          return (
                            <button
                              key={link.title}
                              className="flex flex-col items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                            >
                              <IconComponent className="w-5 h-5 text-slate-600" />
                              <span className="text-xs text-slate-600 text-center">{link.title}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Categories */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                      {searchQuery ? 'Search Results' : 'Browse by Topic'}
                    </h3>
                    {filteredCategories.length > 0 ? (
                      renderCategories()
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <HelpCircle className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p>No articles found for "{searchQuery}"</p>
                        <p className="text-sm mt-1">Try a different search term</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <div className="text-center">
              <p className="text-sm text-slate-600 mb-2">Can't find what you're looking for?</p>
              <Button variant="outline" size="sm" className="w-full">
                <MessageCircle className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
